<template>
    <div class="pane-content">
        <!-- Start Title and Select All -->
        <el-row type="flex" class="mt-2 mb-1">
            <h5 class="status-label">Filters</h5>
        </el-row>
        <el-row>
            <span>
                You can optionally <b>enable filters to include only specific events</b> in your {{ integrationNameWithCasing }} integration.
                When the filters are toggled off, <b>all events from {{ staticName }} will be sent to {{ integrationNameWithCasing }}</b>.
            </span>
        </el-row>
        <el-row class="mt-2"><el-switch v-model="filterStatus" active-color="#00a344" @change="handleFilterStatusChange"></el-switch></el-row>
        <!-- End Title and Select All -->
        <template v-if="filterStatus">
            <hr />
            <!-- Start Direction -->
            <el-row>
                <b>Direction</b>
        </el-row>
        <el-row>
                Select the communication direction you want to include.
            </el-row>
            <el-row class="mt-2">
                <settings-checkbox
                    v-model="isInboundSelected"
                    label="Inbound"
                    @update="updateDirections(CommunicationDirection.INBOUND, $event)"
                />
                <settings-checkbox
                    v-model="isOutboundSelected"
                    label="Outbound"
                    @update="updateDirections(CommunicationDirection.OUTBOUND, $event)"
                />
            </el-row>
            <!-- End Direction -->
            <hr />
            <!-- Start Types -->
            <el-row><b>Type</b></el-row>
            <el-row>Select the communication types you want to include.</el-row>
            <el-row class="mt-2">
                <settings-checkbox
                    v-model="isCallSelected"
                    label="Call"
                    @update="updateTypes(CommunicationType.CALL, $event)"
                />
                <settings-checkbox
                    v-model="isSmsSelected"
                    label="SMS"
                    @update="updateTypes(CommunicationType.SMS, $event)"
                />
                <settings-checkbox
                    v-model="isRvmSelected"
                    label="RVM"
                    @update="updateTypes(CommunicationType.RVM, $event)"
                />
                <settings-checkbox
                    v-model="isEmailSelected"
                    label="Email"
                    @update="updateTypes(CommunicationType.EMAIL, $event)"
                />
                <settings-checkbox
                    v-model="isNoteSelected"
                    label="Note"
                    @update="updateTypes(CommunicationType.NOTE, $event)"
                />
                <settings-checkbox
                    v-model="isSysNoteSelected"
                    label="System Note"
                    @update="updateTypes(CommunicationType.SYSNOTE, $event)"
                />
                <settings-checkbox
                    v-model="isAppointmentSelected"
                    label="Appointment"
                    @update="updateTypes(CommunicationType.APPOINTMENT, $event)"
                />
                <settings-checkbox
                    v-model="isReminderSelected"
                    label="Reminder"
                    @update="updateTypes(CommunicationType.REMINDER, $event)"
                />
            </el-row>
            <!-- End Types -->
            <hr />
            <!-- Start Communication Disposition Status -->
            <el-row><b>Communication Disposition Status</b></el-row>
            <el-row>Select the communication disposition status you want to include.</el-row>
            <el-row class="mt-2">
                <settings-checkbox
                    v-model="isCompletedSelected"
                    label="Completed"
                    @update="updateCommunicationDispositionStatuses(CommunicationDispositionStatus.DISPOSITION_STATUS_COMPLETED_NEW, $event)"
                />
                <settings-checkbox
                    v-model="isAbandonedSelected"
                    label="Abandoned"
                    @update="updateCommunicationDispositionStatuses(CommunicationDispositionStatus.DISPOSITION_STATUS_ABANDONED_NEW, $event)"
                />
                <settings-checkbox
                    v-model="isMissedSelected"
                    label="Missed"
                    @update="updateCommunicationDispositionStatuses(CommunicationDispositionStatus.DISPOSITION_STATUS_MISSED_NEW, $event)"
                />
                <settings-checkbox
                    v-model="isFailedSelected"
                    label="Failed"
                    @update="updateCommunicationDispositionStatuses(CommunicationDispositionStatus.DISPOSITION_STATUS_FAILED_NEW, $event)"
                />
                <settings-checkbox
                    v-model="isPlacedSelected"
                    label="Placed"
                    @update="updateCommunicationDispositionStatuses(CommunicationDispositionStatus.DISPOSITION_STATUS_PLACED_NEW, $event)"
                />
                <settings-checkbox
                    v-model="isSetSelected"
                    label="Set"
                    @update="updateCommunicationDispositionStatuses(CommunicationDispositionStatus.DISPOSITION_STATUS_APPOINTMENT_SET, $event)"
                />
            </el-row>
            <!-- End Communication Disposition Status -->
            <hr />
            <!-- Start Contact -->
            <el-row><b>Contact</b></el-row>
            <el-row>Select the contact types you want to include.</el-row>
            <el-row class="mt-2">
                <settings-checkbox
                    v-model="isAllContactsSelected"
                    label="All Contacts"
                    @update="updateContactType(ContactType.ALL, $event)"
                    unchecked-shape="circle"
                />
                <settings-checkbox
                    v-model="isDisposedContactsSelected"
                    label="Disposed Contacts"
                    @update="updateContactType(ContactType.DISPOSED, $event)"
                    unchecked-shape="circle"
                />
            </el-row>
            <!-- End Contact -->
            <hr />
            <!-- Start Skip Lines -->
            <el-row><b>Skip Lines</b></el-row>
            <el-row>Here you can exclude lines you don't want to send to this integration.</el-row>
            <el-row class="mt-2">
                <campaign-selector
                    v-model="skipLineIds"
                    @change="changeSkipLines"
                    select_style="width: 100%;"
                    multiple>
                </campaign-selector>
            </el-row>
            <!-- End Skip Lines -->
            <hr />
            <!-- Start Duration -->
            <el-row><b>Duration</b></el-row>
            <el-row>Filter by call talk-time or duration.</el-row>
            <el-row class="mt-2" style="display: flex; align-items: center;">
                <span>Don't send calls with</span>
                <el-select v-model="callDurationType" placeholder="Select Duration Type" style="margin: 0 10px;" :clearable="true">
                    <el-option
                        v-for="item in callTypeOptions"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                        class="mx-1">
                    </el-option>
                </el-select>
                <template v-if="callDurationType">
                    <span>less than</span>
                    <el-input-number
                        v-model="callDuration"
                        :min="1"
                        :controls="false"
                        :precision="0"
                        style="margin: 0 10px; width: 15%;"
                    />
                    <span>minutes</span>
                </template>
            </el-row>
            <!-- End Duration -->
            <hr />
            <!-- Start Qualified Contacts -->
            <el-row style="display: flex; justify-content: space-between; align-items: center;">
                <div style="display: flex; flex-direction: column; justify-content: center; width: 100%;">
                    <b>Qualified Contacts</b>
                    <span>Only create contacts when a qualified communication passes the filters.</span>
                </div>
                <el-switch v-model="isQualifiedContactsSelected" type="success" active-color="#00a344" />
        </el-row>
            <!-- End Qualified Contacts -->
        </template>
    </div>
</template>

<script>
import SettingsCheckbox from './settings-checkbox.vue'
import * as CommunicationDirection from '../../constants/communication-direction'
import * as CommunicationType from '../../constants/communication-types'
import * as CommunicationDispositionStatus from '../../constants/communication-disposition-status'

export const ContactType = Object.freeze({
    ALL: 'all',
    DISPOSED: 'disposed_contacts'
})

const callTypeOptions = [
    { value: 'duration', label: 'Duration'},
    { value: 'talk_time', label: 'Talk Time'}
]

export default {
    components: {
        SettingsCheckbox
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        statics: {
            type: Object,
            required: true
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            /** Constants **/
            CommunicationDirection,
            CommunicationType,
            CommunicationDispositionStatus,
            ContactType,
            callTypeOptions,

            /** UI **/
            // Direction
            isInboundSelected: false,
            isOutboundSelected: false,
            directions: [],
            filterStatus: false,

            // Types
            isCallSelected: false,
            isSmsSelected: false,
            isRvmSelected: false,
            isEmailSelected: false,
            isNoteSelected: false,
            isSysNoteSelected: false,
            isAppointmentSelected: false,
            isReminderSelected: false,
            types: [],

            // Communication Disposition Status
            isCompletedSelected: false,
            isAbandonedSelected: false,
            isMissedSelected: false,
            isFailedSelected: false,
            isPlacedSelected: false,
            isSetSelected: false, // For Appointment Set
            communicationDispositionStatuses: [],

            // Contact
            isAllContactsSelected: true,
            isDisposedContactsSelected: false,
            contactType: '',

            // Skip Lines
            skipLineIds: [],

            // Duration
            callDuration: null,
            callDurationType: '',

            // Qualified Contacts
            isQualifiedContactsSelected: false,
        }
    },
    computed: {
        staticName() {
            return this.statics.name || 'Aloware'
        }
    },
    methods: {
        updateDirections(direction, isSelected) {
            const directionValue = Number(direction)

            this.directions = isSelected
                ? [...new Set([...this.directions, directionValue])]
                : this.directions.filter(d => d !== directionValue)

            // Update the model with the new directions array
            this.model.settings.filters.direction = [...this.directions]

            // Check if all available directions are selected
            const allDirectionsSelected = [CommunicationDirection.INBOUND, CommunicationDirection.OUTBOUND]
                .every(dir => this.directions.includes(dir))

            // Set filter_direction to false only if all directions are selected
            if (allDirectionsSelected) {
                this.model.settings.filters.filter_direction = false
                return
            }

            // At this point, either no directions or some directions are selected, so enable the filter
            this.model.settings.filters.filter_direction = true
        },
        updateTypes(type, isSelected) {
            const typeValue = Number(type)
            this.types = isSelected
                ? [...new Set([...this.types, typeValue])]
                : this.types.filter(t => t !== typeValue)

            // Update the model with the new types array
            this.model.settings.filters.type = [...this.types]

            // Check if all available types are selected
            const allTypesSelected = [
                CommunicationType.CALL,
                CommunicationType.SMS,
                CommunicationType.RVM,
                CommunicationType.EMAIL,
                CommunicationType.NOTE,
                CommunicationType.SYSNOTE,
                CommunicationType.APPOINTMENT,
                CommunicationType.REMINDER
            ].every(t => this.types.includes(t))

            // Set filter_type to false only if all types are selected
            if (allTypesSelected) {
                this.model.settings.filters.filter_type = false
                return
            }

            // At this point, either no types or some types are selected, so enable the filter
            this.model.settings.filters.filter_type = true
        },
        updateCommunicationDispositionStatuses(status, isSelected) {
            const statusValue = Number(status)

            this.communicationDispositionStatuses = isSelected
                ? [...new Set([...this.communicationDispositionStatuses, statusValue])]
                : this.communicationDispositionStatuses.filter(s => s !== statusValue)

            // Update the model with the new statuses array
            this.model.settings.filters.communication_disposition_statuses = [...this.communicationDispositionStatuses]

            // Check if all available statuses are selected
            const allStatusesSelected = [
                CommunicationDispositionStatus.DISPOSITION_STATUS_COMPLETED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_ABANDONED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_MISSED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_FAILED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_PLACED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_APPOINTMENT_SET,
            ].every(s => this.communicationDispositionStatuses.includes(s))

            // Set filter_communication_disposition_status to false only if all statuses are selected
            if (allStatusesSelected) {
                this.model.settings.filters.filter_communication_disposition_status = false
                return
            }

            // At this point, either no statuses or some statuses are selected, so enable the filter
            this.model.settings.filters.filter_communication_disposition_status = true
        },
        updateContactType(type, isSelected) {
            /** Only one type can be selected at a time */

            // First, set all contact types to false
            this.isAllContactsSelected = false
            this.isDisposedContactsSelected = false

            // If selecting a new type, update it
            if (isSelected) {
                this.contactType = type

                switch (type) {
                    case ContactType.ALL:
                        this.isAllContactsSelected = true
                        break
                    case ContactType.DISPOSED:
                        this.isDisposedContactsSelected = true
                        break
                }
            } else {
                // If unchecking the current type, clear the type
                if (this.contactType === type) {
                    this.contactType = ''
                } else {
                    // If trying to uncheck a different type, keep the current selection
                    switch (this.contactType) {
                        case ContactType.ALL:
                            this.isAllContactsSelected = true
                            break
                        case ContactType.DISPOSED:
                            this.isDisposedContactsSelected = true
                            break
                    }
                }
            }

            this.model.settings.filters.contacts = this.contactType
            this.model.settings.filters.filter_disposed_contacts = (this.contactType !== ContactType.ALL)
        },
        changeSkipLines(val) {
            this.skipLineIds = val
            this.model.settings.filters.skip_line_ids = val
            this.model.settings.filters.filter_lines = val.length > 0
        },
        setToRecommendedValues() {
            // Initialize Direction with Inbound and Outbound selected
            this.directions = [CommunicationDirection.INBOUND, CommunicationDirection.OUTBOUND]
            this.model.settings.filters.direction = [...this.directions]
            this.isInboundSelected = true
            this.isOutboundSelected = true
            this.model.settings.filters.filter_direction = true

            // Initialize Types with all types selected
            this.types = [
                CommunicationType.CALL,
                CommunicationType.SMS,
                CommunicationType.RVM,
                CommunicationType.EMAIL,
                CommunicationType.APPOINTMENT,
                CommunicationType.NOTE,
                CommunicationType.SYSNOTE,
                CommunicationType.REMINDER,
            ]
            this.model.settings.filters.type = [...this.types]
            this.isCallSelected = true
            this.isSmsSelected = true
            this.isRvmSelected = true
            this.isEmailSelected = true
            this.isNoteSelected = true
            this.isSysNoteSelected = true
            this.isAppointmentSelected = true
            this.isReminderSelected = true
            this.model.settings.filters.filter_type = true

            // Initialize Communication Disposition Status with all selected
            this.communicationDispositionStatuses = [
                CommunicationDispositionStatus.DISPOSITION_STATUS_COMPLETED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_ABANDONED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_MISSED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_FAILED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_PLACED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_APPOINTMENT_SET,
            ]
            this.model.settings.filters.communication_disposition_statuses = [...this.communicationDispositionStatuses]
            this.isCompletedSelected = true
            this.isAbandonedSelected = true
            this.isMissedSelected = true
            this.isFailedSelected = true
            this.isPlacedSelected = true
            this.isSetSelected = true // For Appointment Set
            this.model.settings.filters.filter_communication_disposition_status = true

            // Initialize Contact with All Contacts selected
            this.contactType = ContactType.ALL
            this.model.settings.filters.contacts = ContactType.ALL
            this.isAllContactsSelected = true
            this.isDisposedContactsSelected = false

            // Initialize Skip Lines with none selected
            this.skipLineIds = []
            this.model.settings.filters.skip_line_ids = []
            this.model.settings.filters.filter_lines = false

            this.callDuration = 1
            this.callDurationType = null
            this.model.settings.filters.filter_call_duration = false
            this.model.settings.filters.call_duration = this.callDuration
            this.model.settings.filters.call_duration_type = this.callDurationType

            // Initialize Qualified Contacts as disabled
            this.isQualifiedContactsSelected = false
            this.model.settings.filters.filter_qualified_only = false
        },
        handleFilterStatusChange(newVal) {
            this.model.settings.filters.status = newVal

            // If filter status is enabled, we set the default values
            if (newVal) {
                this.setToRecommendedValues()
            }
        },
    },
    watch: {
        callDurationType() {
            this.model.settings.filters.call_duration_type = this.callDurationType
            this.model.settings.filters.filter_call_duration = !!this.callDurationType

            // Set the call duration to null if the call duration type is not selected
            if (!this.callDurationType) {
                this.callDuration = null
                this.model.settings.filters.call_duration = null
                this.model.settings.filters.filter_call_duration = false
            }
        },
        callDuration() {
            this.model.settings.filters.call_duration = this.callDuration
        },
        isQualifiedContactsSelected() {
            this.model.settings.filters.filter_qualified_only = this.isQualifiedContactsSelected
        },
    },
    created() {
        /** Initialize the filter status toggle switch */
        this.filterStatus = this.model.settings.filters.status

        /**  Initialize Direction Checkboxes state */
        if (!this.model.settings.filters.filter_direction) {
            // If filter_direction is false, select all directions
            this.directions = [CommunicationDirection.INBOUND, CommunicationDirection.OUTBOUND]
            this.model.settings.filters.direction = [...this.directions]
        } else {
            // If filter_direction is true, use the saved directions
            this.directions = [...(this.model.settings.filters.direction || [])]
        }
        this.isInboundSelected = this.directions.some(d => d === CommunicationDirection.INBOUND)
        this.isOutboundSelected = this.directions.some(d => d === CommunicationDirection.OUTBOUND)

        /** Initialize Types (Communication) Checkboxes state */
        if (!this.model.settings.filters.filter_type) {
            // If filter_type is false, select all types
            this.types = [
                CommunicationType.CALL,
                CommunicationType.SMS,
                CommunicationType.RVM,
                CommunicationType.EMAIL,
                CommunicationType.NOTE,
                CommunicationType.SYSNOTE,
                CommunicationType.APPOINTMENT,
                CommunicationType.REMINDER
            ]
            this.model.settings.filters.type = [...this.types]
            this.isCallSelected = true
            this.isSmsSelected = true
            this.isRvmSelected = true
            this.isEmailSelected = true
            this.isNoteSelected = true
            this.isSysNoteSelected = true
            this.isAppointmentSelected = true
            this.isReminderSelected = true
        } else {
            // If filter_type is true, use the saved types
            this.types = [...(this.model.settings.filters.type || [])]
            this.isCallSelected = this.types.some(t => t === CommunicationType.CALL)
            this.isSmsSelected = this.types.some(t => t === CommunicationType.SMS)
            this.isRvmSelected = this.types.some(t => t === CommunicationType.RVM)
            this.isEmailSelected = this.types.some(t => t === CommunicationType.EMAIL)
            this.isNoteSelected = this.types.some(t => t === CommunicationType.NOTE)
            this.isSysNoteSelected = this.types.some(t => t === CommunicationType.SYSNOTE)
            this.isAppointmentSelected = this.types.some(t => t === CommunicationType.APPOINTMENT)
            this.isReminderSelected = this.types.some(t => t === CommunicationType.REMINDER)
        }

        // Initialize Communication Disposition Status Checkboxes state
        if (!this.model.settings.filters.filter_communication_disposition_status) {
            // If filter_communication_disposition_status is false, select all statuses
            this.communicationDispositionStatuses = [
                CommunicationDispositionStatus.DISPOSITION_STATUS_COMPLETED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_ABANDONED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_MISSED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_FAILED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_PLACED_NEW,
                CommunicationDispositionStatus.DISPOSITION_STATUS_APPOINTMENT_SET,
            ]
            this.model.settings.filters.communication_disposition_statuses = [...this.communicationDispositionStatuses]
            this.isCompletedSelected = true
            this.isAbandonedSelected = true
            this.isMissedSelected = true
            this.isFailedSelected = true
            this.isPlacedSelected = true
            this.isSetSelected = true // For Appointment Set
        } else {
            // If filter_communication_disposition_status is true, use the saved statuses
            this.communicationDispositionStatuses = [...(this.model.settings.filters.communication_disposition_statuses || [])]
            this.isCompletedSelected = this.communicationDispositionStatuses.some(s => s === CommunicationDispositionStatus.DISPOSITION_STATUS_COMPLETED_NEW)
            this.isAbandonedSelected = this.communicationDispositionStatuses.some(s => s === CommunicationDispositionStatus.DISPOSITION_STATUS_ABANDONED_NEW)
            this.isMissedSelected = this.communicationDispositionStatuses.some(s => s === CommunicationDispositionStatus.DISPOSITION_STATUS_MISSED_NEW)
            this.isFailedSelected = this.communicationDispositionStatuses.some(s => s === CommunicationDispositionStatus.DISPOSITION_STATUS_FAILED_NEW)
            this.isPlacedSelected = this.communicationDispositionStatuses.some(s => s === CommunicationDispositionStatus.DISPOSITION_STATUS_PLACED_NEW)
            this.isSetSelected = this.communicationDispositionStatuses.some(s => s === CommunicationDispositionStatus.DISPOSITION_STATUS_APPOINTMENT_SET)
        }

        // Initialize Contact Checkboxes state
        this.contactType = this.model.settings.filters.contacts || ContactType.ALL
        this.isAllContactsSelected = this.contactType === ContactType.ALL
        this.isDisposedContactsSelected = this.contactType === ContactType.DISPOSED

        // Initialize Skip Lines Checkboxes state
        this.skipLineIds = this.model.settings.filters.skip_line_ids || []
        this.model.settings.filters.filter_lines = this.skipLineIds.length > 0

        this.callDuration = this.model.settings.filters.call_duration || 1
        this.callDurationType = this.model.settings.filters.call_duration_type || ''

        this.isQualifiedContactsSelected = this.model.settings.filters.filter_qualified_only || false
    }
}
</script>

<style scoped>
.status-label {
    margin: 0 12px 0 0;
    font-size: 20px;
}

.pane-content {
    display: flex;
    flex-direction: column;
    padding-left: 8px;
    padding-right: 8px;
}

.custom-checkbox input {
    display: none;
}

.custom-checkbox i {
    font-size: 16px;
    margin-right: 8px;
    opacity: 0;
    transition: opacity 0.2s;
    color: #00854A;
}

.custom-checkbox input:checked + i {
    opacity: 1;
}

.custom-checkbox input:checked {
    color: #00854A;
}
</style>
